import React, { Component } from 'react'
import { AxiosResponse } from "axios";
import { connect } from 'react-redux';

import WatermarkMarkerCommon, { ObjectsType } from "../products/components/watermark-marker-common";
import { WatermarkGroupType } from "../products/types";
import { URL } from "app/config/api-config";
import { godProjectId, isGodModeOn, projectId, saveGroupImageWatermarkObjects } from 'app/helpers/api';
import { echo } from "app/components/websocketeer";

import {
    Grid,
    GridCell,
    Button,
    Icon,
    css,
    Theme,
} from '@bdfx/mergado-ui-kit';

import { ReduxList } from 'app/types/app-types';
import { isJson } from 'app/helpers/other';
import { find } from 'lodash';

type WebSocketMessage = {
    is_alt: boolean
    image_or_alt_image_hash: string
    project_id: number
    group_id: number
    group_image_id: number
    shard: string
    status: string
    status_reason: string
    base64_image_src: string
}

interface Props {
    isAlt: boolean
    selectedGroupId: number
    initialIndex: number
    updateWatermarkGroups: Function
    close: Function
    watermarkGroups: ReduxList<WatermarkGroupType[]>
}

interface State {
    isSaving: boolean
    isWaitingForResult: boolean
    imageBlob: string
    selectedIndex: number
}

class EditMaskWrapper extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isSaving: false,
            isWaitingForResult: false,
            imageBlob: null,
            selectedIndex: props.initialIndex,
        }

        const actualProjectId = isGodModeOn ? godProjectId : projectId

        echo.channel(`private-project.${actualProjectId}`)
            .listen('.watermark-group-image.started', (event) => this.handleProcessStarted(event))

        echo.channel(`private-project.${actualProjectId}`)
            .listen('.watermark-group-image.removed', (event) => this.handleProcessDone(event))
    }

    findGroupById = () => {
        const { selectedGroupId, watermarkGroups } = this.props
        return selectedGroupId ? find(watermarkGroups?.data, (watermarkGroup) => watermarkGroup.id === selectedGroupId) : null
    }

    getSelectedImage = (selectedIndex: number) => {
        const selectedGroup = this.findGroupById()
        return selectedGroup?.images && selectedIndex !== null ? selectedGroup.images[selectedIndex] : null
    }

    handleProcessStarted = (message: WebSocketMessage) => {
        const { selectedIndex } = this.state
        const watermarkGroupImage = this.getSelectedImage(selectedIndex)
        if (message.image_or_alt_image_hash === watermarkGroupImage.image_or_alt_image_hash) {
            console.debug('handle inpainting process started', message)
        }
    }

    handleProcessDone = (message: WebSocketMessage) => {
        const { selectedIndex } = this.state
        const watermarkGroupImage = this.getSelectedImage(selectedIndex)
        if (message.image_or_alt_image_hash === watermarkGroupImage.image_or_alt_image_hash) {
            console.debug(`handle inpainting process done for group ${message.group_id} image ${message.image_or_alt_image_hash} status ${message.status}`)
            this.setState({ imageBlob: message.base64_image_src, isWaitingForResult: false })
            this.refreshResultImage()
        }
    }

    refreshResultImage = () => {
        this.props.updateWatermarkGroups()
    }

    saveObjects = (objects: ObjectsType, watermark_group_image_id: number, isCloseOnSave: boolean) => {
        if (this.state.isSaving) {
            return;
        }
        this.setState({ isSaving: true, isWaitingForResult: true })
        saveGroupImageWatermarkObjects(watermark_group_image_id, objects)
            .then((response: AxiosResponse<any>) => {
                this.refreshResultImage()
            })
            .catch(e => {
                console.error('cannot save group image mask', e)
            })
            .finally(() => {
                this.setState({ isSaving: false })
                if (!objects || objects.length === 0) {
                    this.setState({ isWaitingForResult: false, imageBlob: null })
                }
                if (isCloseOnSave) {
                    this.props.close()
                }
            })
    }

    clearImageBlob = () => {
        this.setState({ imageBlob: null })
    }

    prevImage = () => {
        const { selectedIndex } = this.state
        if (selectedIndex > 0) {
            const newIndex = selectedIndex - 1
            this.setState({ selectedIndex: newIndex })
        }
    }

    nextImage = () => {
        const { selectedIndex } = this.state

        const selectedGroup = this.findGroupById()
        const lastImageIndex = selectedGroup.images.length - 1

        if (selectedIndex < lastImageIndex) {
            const newIndex = selectedIndex + 1
            this.setState({
                selectedIndex: newIndex,
            })
        }
    }

    render() {
        const { imageBlob, selectedIndex } = this.state

        const selectedGroup = this.findGroupById()
        const watermarkGroupImage = this.getSelectedImage(selectedIndex)
        const t = new Date().getTime()

        const parsedObjects = isJson(watermarkGroupImage?.user_set_objects) ? JSON.parse(watermarkGroupImage.user_set_objects) : null
        /* console.debug(`render objects, image index ${selectedIndex}, objects to render: `, parsedObjects ? parsedObjects.length : null) */
        const imageSourceUrl = `${URL.laravelBase}get-source-image/${selectedGroup.project_id}/${watermarkGroupImage.image_or_alt_image_hash}`
        const imageUrl = `${URL.laravelBase}get-wmgroup-image/${selectedGroup.project_id}/${selectedGroup.id}/${watermarkGroupImage.filename}?t=${watermarkGroupImage.image_or_alt_image_hash}`
        const inpaintedImageUrl = `${URL.laravelBase}get-wmgroup-image-inpainted/${selectedGroup.project_id}/${selectedGroup.id}/${watermarkGroupImage.filename}?t=${watermarkGroupImage.image_or_alt_image_hash}${t}`

        const isTheFirst = selectedIndex === 0
        const isTheLast = selectedIndex === selectedGroup.images.length - 1

        const prevNextIconSize = 22

        return (
            <StyledGrid cols={'50px auto 50px'}>
                <GridCell className={'prev-link'}>
                    {!isTheFirst &&
                        <Button
                            type={'void'}
                            secondary={true}
                            color={'nocolor'}
                            onClick={() => this.prevImage()}>
                            {<Icon type={selectedIndex < 1 ? 'chevrons-left-light' : 'chevron-left'} size={prevNextIconSize} color={Theme.blue} />}
                        </Button>}
                </GridCell>

                <GridCell>
                    <WatermarkMarkerCommon
                        is_alt={this.props.isAlt}
                        markerType={'wmremove-group-image'}
                        saveObjects={this.saveObjects}
                        clearImageBlob={this.clearImageBlob}
                        closeModal={this.props.close}
                        altLevel={null}
                        image_url={imageUrl}
                        image_url_hash={null}
                        image_width={watermarkGroupImage.width}
                        image_height={watermarkGroupImage.height}
                        image_hash={watermarkGroupImage.image_or_alt_image_hash}
                        group_id={watermarkGroupImage.watermark_group_id}
                        watermark_group_image_id={watermarkGroupImage.id}
                        watermark_group_image_filename={watermarkGroupImage.filename}
                        user_set_objects={watermarkGroupImage.user_set_objects}
                        mask_hash={watermarkGroupImage.mask_hash}
                        imageBlob={imageBlob ? imageBlob : (watermarkGroupImage.user_set_objects ? inpaintedImageUrl : imageUrl)}
                        isWaitingForResult={this.state.isWaitingForResult || this.state.isSaving}
                        showClose={true}
                    />
                </GridCell>

                <GridCell className={'next-link'}>
                    {!isTheLast &&
                        <Button
                            secondary={true}
                            type={'void'}
                            color={'nocolor'}
                            onClick={() => this.nextImage()}>
                            {<Icon type={selectedIndex > 8 ? 'chevrons-right-light' : 'chevron-right'} size={prevNextIconSize} color={Theme.blue} />}
                        </Button>
                    }
                </GridCell>
            </StyledGrid>
        )
    }
}

const StyledGrid = css(Grid)({
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
    }
})

function mapStateToProps(state) {
    return {
        watermarkGroups: state.watermarkGroups,
    }
}

export default connect(mapStateToProps, {
})(EditMaskWrapper)

